import { css, cx } from "@emotion/css";
import { CircularProgress, InlineSnackbar } from "@octopusdeploy/design-system-components";
import { LockOnIcon } from "@octopusdeploy/design-system-icons";
import type { FormFieldProps, DeprecatedDropdownMenuOption } from "@octopusdeploy/design-system-octopus-components";
import { DeprecatedSelect } from "@octopusdeploy/design-system-octopus-components";
import { space, themeTokens } from "@octopusdeploy/design-system-tokens";
import type { CertificateResource } from "@octopusdeploy/octopus-server-client";
import { useTrackEvent } from "@octopusdeploy/portal-analytics";
import cn from "classnames";
import moment from "moment";
import { useState } from "react";
import { createAddCertificateAnalyticsEvent } from "~/areas/library/components/Certificates/Certificate/amplitudeAnalytics";
import { useEphemeralEnvironmentsEnabled } from "~/areas/projects/components/EphemeralEnvironments/useEphemeralEnvironmentsEnabled";
import { repository } from "~/clientInstance";
import { useDoBusyTaskEffect } from "~/components/DataBaseComponent/index";
import OpenDialogIconButton from "~/components/Dialog/OpenDialogIconButton";
import { IconButtonWithTooltip } from "~/components/IconButtonWithTooltip";
import InputWithActions from "~/components/InputWithActions/InputWithActions";
import { useOctopusTheme } from "~/components/Theme";
import type { AddCertificateWorkflowInput } from "~/workflows/AddCertificateWorkflowInput";
import { useWorkflow } from "~/workflows/useWorkflow";
import type { Workflow } from "~/workflows/workflows";
import { workflows } from "~/workflows/workflows";
import CertificateSelector from "./CertificateSelector";
interface CertificateSelectProps extends FormFieldProps<string> {
    allowClear?: boolean;
    tenantId?: string;
    disabled?: boolean;
    label?: string | JSX.Element;
    error?: string | null;
    warning?: string;
    hideSearch?: boolean;
    items(): Promise<CertificateResource[]>;
    onRequestRefresh(): Promise<boolean>;
    validate?(value: string | null): string;
    onValidate?(value: string): void;
    doBusyTask(action: () => Promise<void>): Promise<boolean>;
    addCertificateWorkflow?: Workflow<AddCertificateWorkflowInput, CertificateResource>;
}
function CertificateSelect(props: CertificateSelectProps) {
    const { onChange, onValidate, tenantId, doBusyTask, items, onRequestRefresh, hideSearch, ...otherProps } = props;
    const [loadedItems, setLoadedItems] = useState<CertificateResource[] | undefined>(undefined);
    const [busy, setBusy] = useState<boolean>(true);
    const [error, setError] = useState<string | undefined>(undefined);
    const [showSnackbar, setShowSnackbar] = useState(false);
    const theme = useOctopusTheme();
    const ephemeralEnvironmentsAreEnabled = useEphemeralEnvironmentsEnabled();
    const startAddCertificateWorkflow = useWorkflow(props.addCertificateWorkflow ?? workflows.addCertificateWorkflow);
    useDoBusyTaskEffect(doBusyTask, async () => {
        setLoadedItems(await items());
        setBusy(false);
    }, []);
    const onCertificateCreated = async (newCertificate: CertificateResource) => {
        await refreshData();
        props.onChange(newCertificate.Id);
        setTimeout(() => {
            setShowSnackbar(true);
        }, 300);
    };
    const handleChange = (certificateId: string | undefined) => {
        const value = certificateId === "" ? null : certificateId;
        if (props.validate) {
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            const result = props.validate(value!);
            setError(result);
            if (props.onValidate) {
                props.onValidate(result);
            }
        }
        if (props.onChange) {
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            props.onChange(value!);
        }
    };
    const refreshData = async () => {
        try {
            setBusy(true);
            await props.onRequestRefresh();
            setLoadedItems(await props.items());
        }
        finally {
            setBusy(false);
        }
    };
    if (!loadedItems) {
        return <CircularProgress size="small"/>;
    }
    const selectItems: DeprecatedDropdownMenuOption[] = loadedItems.map((certificate) => {
        const expired = isCertificateExpired(certificate);
        const icon = expired ? <LockOnIcon size={24} color={theme.danger}/> : <LockOnIcon size={24}/>;
        return {
            value: certificate.Id,
            text: certificate.Name + (expired ? " (expired)" : ""),
            icon,
            variant: expired ? "danger" : undefined,
        };
    });
    const selectionRenderer = (certificateId: string) => {
        const certificate = loadedItems.find((cert) => cert.Id === certificateId);
        if (!certificate) {
            return certificateId;
        }
        if (isCertificateExpired(certificate)) {
            return (<div className={cx(iconWrapper, expiredIcon)}>
                    <span className={selectedIcon}>
                        <LockOnIcon size={24}/>
                    </span>
                    <span>{certificate.Name} (expired)</span>
                </div>);
        }
        return (<div className={iconWrapper}>
                <span className={selectedIcon}>
                    <LockOnIcon size={24}/>
                </span>
                <span>{certificate.Name}</span>
            </div>);
    };
    return (<>
            <InputWithActions input={<DeprecatedSelect label="Select certificate" {...otherProps} allowFilter={true} onChange={handleChange} items={selectItems} selectionRenderer={selectionRenderer}/>} busy={busy} actions={<>
                        <SearchDialogButton selectedId={props.value} onSelect={(certificate) => handleChange(certificate.Id)} tenantId={props.tenantId} hide={hideSearch}/>
                        <IconButtonWithTooltip disabled={busy} onClick={refreshData} toolTipContent={"Refresh"} icon={"ArrowRefreshIcon"} accessibleName={"Refresh"}/>
                        <AddCertificateButton startWorkflow={() => startAddCertificateWorkflow({
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                spaceId: repository.spaceId!,
                initiallySelectedTenantIds: props.tenantId ? [props.tenantId] : [],
                ephemeralEnvironmentsAreEnabled,
            }, onCertificateCreated)}/>
                    </>}/>
            <div className={cn({ snackbarContainerStyles: showSnackbar })}>
                <InlineSnackbar variant="success" show={showSnackbar} content={"Certificate Created"} onClose={() => setShowSnackbar(false)} autoHideDuration={3000}/>
            </div>
        </>);
}
function isCertificateExpired(certificate: CertificateResource) {
    const now = moment();
    const certificateExpiry = moment(certificate.NotAfter);
    return certificateExpiry.isBefore(now);
}
const selectedIcon = css({
    position: "relative",
    paddingRight: space[6],
});
const expiredIcon = css({
    color: themeTokens.color.text.danger,
});
const iconWrapper = css({
    display: "flex",
    alignItems: "center",
});
function AddCertificateButton(props: {
    startWorkflow: () => void;
}) {
    const trackEvent = useTrackEvent();
    const handleOnClick = () => {
        trackEvent(createAddCertificateAnalyticsEvent("Drawer"));
        props.startWorkflow();
    };
    return <IconButtonWithTooltip toolTipContent={"Add"} onClick={handleOnClick} icon={"PlusIcon"} accessibleName={"Add certificate"}/>;
}
interface SearchDialogButtonProps {
    selectedId: string;
    onSelect: (pool: CertificateResource) => void;
    tenantId?: string;
    hide?: boolean;
}
function SearchDialogButton({ selectedId, onSelect, tenantId, hide }: SearchDialogButtonProps) {
    const ephemeralEnvironmentsAreEnabled = useEphemeralEnvironmentsEnabled();
    if (hide) {
        return null;
    }
    return (<OpenDialogIconButton toolTipContent="Search" wideDialog={true} icon={"MagnifyingGlassIcon"} accessibleName={"Search"}>
            <CertificateSelector onSelected={onSelect} selectedCertificateId={selectedId} tenantId={tenantId} ephemeralEnvironmentsAreEnabled={ephemeralEnvironmentsAreEnabled}/>
        </OpenDialogIconButton>);
}
export default CertificateSelect;
